// React Utils
import { createPortal } from "react-dom";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
// Packages components
import { motion as Motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Components
import NotifyHeader from "./NotifyHeader";
// Redux
import { fetchMyNotificaciones, markNotificationAsRead } from "@/features/notificacion/notificacionThunks";
// Utils
import { scrollToTop } from "@/lib/utilsUI";

const FILTERS = [
  { label: "Recientes", value: "recientes" },
  { label: "Ayer", value: "ayer" },
  { label: "Esta semana", value: "semana" },
  { label: "Semana anterior", value: "semana-anterior" },
];
const TABS = [
  { label: "Todas", value: "todas" },
  { label: "Any", value: "any" },
  { label: "Promos", value: "promos" },
  { label: "Descuentos", value: "descuentos" },
  { label: "Embajador", value: "embajador" },
  { label: "Empresas", value: "empresas" },
];

export default function NotificationModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("recientes");
  const [tab, setTab] = useState("todas");
  
  // Obtener notificaciones del Redux store
  const { 
    notifications, 
    pagination, 
    isLoadingNotifications,
    error 
  } = useSelector((state) => state.notificacion);

  // Cargar notificaciones cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      // Resetear a página 1 cuando se abre
      dispatch(fetchMyNotificaciones({ 
        page: 1, 
        limit: 20,
        type: tab === 'todas' ? undefined : tab,
      }));
    }
  }, [isOpen, tab, dispatch]);

  // Función para cargar más notificaciones
  const handleLoadMore = () => {
    if (pagination.hasNextPage && !isLoadingNotifications) {
      dispatch(fetchMyNotificaciones({ 
        page: pagination.currentPage + 1, 
        limit: 20,
        type: tab === 'todas' ? undefined : tab,
        append: true, // Indica que debe agregar, no reemplazar
      }));
    }
  };

  // Función para manejar clic en notificación
  const handleNotificationClick = (notification) => {
    // Marcar como leída si no está leída
    if (notification.read === 0) {
      dispatch(markNotificationAsRead(notification.id));
    }
    
    // Cerrar modal y navegar
    if (notification.url) {
      onClose();
      scrollToTop();
    }
  };

  // Filtrado de notificaciones por tipo (tabs)
  const filtered = notifications.filter((n) => {
    if (tab === "todas") return true;
    if (tab === "any") return n.type === "any";
    // Agregar más tipos cuando se implementen
    return true;
  });

  // Agrupar por fecha
  const groupByDate = filtered.reduce((acc, n) => {
    acc[n.date] = acc[n.date] || [];
    acc[n.date].push(n);
    return acc;
  }, {});

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <Motion.div
          key="notify-modal-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[85] bg-aw-bluedark sm:bg-aw-bluedark-50 flex items-start justify-end"
          onClick={onClose}
        >
          <Motion.div
            key="notify-modal"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "any",
              url: `/perfil/${localStorage.getItem("uid")}`, stiffness: 300, damping: 30 }}
            className="w-full max-w-md h-[92.5dvh] sm:h-full bg-aw-bluedark rounded-l-2xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header: título y filtro de tiempo */}
            <div className="px-4 pt-2 pb-2 flex flex-col gap-2">
              <NotifyHeader
                onClose={onClose}
                filters={FILTERS}
                filter={filter}
                setFilter={setFilter}
              />

              {/* Tabs de filtro */}
              {/* <div className="flex gap-2 mt-2 flex-wrap">
                {TABS.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => setTab(t.value)}
                    className={`px-4 py-1 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                      tab === t.value
                        ? t.value === "todas"
                          ? "bg-aw-green text-black border-aw-green"
                          : "bg-aw-green text-black border-aw-green"
                        : "bg-aw-bluedark-90 text-gray-300 border-aw-bluedark-70 hover:bg-aw-bluedark-70 hover:text-white"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div> */}
            </div>
            {/* Lista de notificaciones */}
            <div className="flex-1 overflow-y-auto px-6 pb-6 scrollbar-box">
              {isLoadingNotifications && notifications.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-400">Cargando notificaciones...</div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-red-400">Error: {error}</div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-400">No tienes notificaciones</div>
                </div>
              ) : (
                <>
                  {Object.entries(groupByDate).map(([date, notifs]) => (
                    <div key={date} className="mb-4">
                      <div className="text-base font-bold text-white mb-2">
                        {date === "hoy" ? "Hoy" : date === "anterior" ? "Anteriores" : date}
                      </div>
                      {notifs.map((n) => (
                        <Link
                          to={n.url || "#"}
                          onClick={() => handleNotificationClick(n)}
                          key={`actividad-${n.id}`}
                          className={`flex items-center gap-3 mb-3 p-2 rounded-lg transition-colors ${
                            n.read === 0 ? "bg-aw-bluedark-70" : "hover:bg-aw-bluedark-90"
                          }`}
                        >
                          <div
                            className={`flex items-center justify-center w-8 h-8 rounded-full ${
                              n.read === 0 ? "bg-aw-green" : "bg-gray-600"
                            }`}
                          >
                            <FontAwesomeIcon icon={"fa-solid fa-bell"} size="xs" />
                          </div>
                          <div className="flex-1">
                            <div className={`text-xs ${n.read === 0 ? "text-white font-semibold" : "text-gray-300"} line-clamp-3`}>
                              <span className={`text-aw-green`}>{n.emisor}</span>{" "}
                              {n.contenido}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ))}
                  
                  {/* Botón Cargar más */}
                  {pagination.hasNextPage && (
                    <div className="flex justify-center mt-4 mb-4">
                      <button
                        onClick={handleLoadMore}
                        disabled={isLoadingNotifications}
                        className="px-6 py-2 bg-aw-green text-black rounded-full font-semibold hover:bg-aw-green-80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoadingNotifications ? "Cargando..." : "Cargar más"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Motion.div>
        </Motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
